import { useEffect, useState } from 'react';
import { useTranslate } from '@refinedev/core';
import type { CrudFilter } from '@refinedev/core';
import { Select } from 'antd';
import { FilterPanel } from '@/components/common/FilterPanel';
import { getFilterValue, type SetFilters } from '@/utils/filters';
import { ASSET_FILTER_FIELDS } from '@/pages/content_catalog/products/constants';

type Pending = {
  is_cover_downloaded?: boolean;
  is_master_downloaded?: boolean;
};

interface ProductAssetsFilterProps {
  filters: CrudFilter[];
  confirm: () => void;
  clearFilters?: () => void;
  setFilters: SetFilters;
}

/** One dropdown for the two asset facets; each applies as `field[eq]`. */
export const ProductAssetsFilter = ({
  filters,
  confirm,
  clearFilters,
  setFilters,
}: ProductAssetsFilterProps) => {
  const t = useTranslate();
  const f = (key: string) => t(`content_catalog.products.assets.${key}`);

  const current: Pending = {
    is_cover_downloaded: getFilterValue<boolean>(
      filters,
      'is_cover_downloaded',
      'eq'
    ),
    is_master_downloaded: getFilterValue<boolean>(
      filters,
      'is_master_downloaded',
      'eq'
    ),
  };
  const [pending, setPending] = useState<Pending>(current);
  useEffect(
    () => setPending(current),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [current.is_cover_downloaded, current.is_master_downloaded]
  );

  const apply = (values: Pending) =>
    setFilters(
      ASSET_FILTER_FIELDS.map((field) => ({
        field,
        operator: 'eq',
        value: values[field],
      })),
      'merge'
    );

  const yesNo = [
    { label: t('common.yes'), value: true },
    { label: t('common.no'), value: false },
  ];

  return (
    <FilterPanel
      onFilter={() => {
        apply(pending);
        confirm();
      }}
      onReset={() => {
        setPending({});
        apply({});
        clearFilters?.();
      }}
    >
      <Select
        allowClear
        placeholder={f('cover')}
        style={{ minWidth: 180 }}
        value={pending.is_cover_downloaded}
        onChange={(v?: boolean) =>
          setPending((p) => ({ ...p, is_cover_downloaded: v }))
        }
        options={yesNo}
      />
      <Select
        allowClear
        placeholder={f('master')}
        style={{ minWidth: 180 }}
        value={pending.is_master_downloaded}
        onChange={(v?: boolean) =>
          setPending((p) => ({ ...p, is_master_downloaded: v }))
        }
        options={yesNo}
      />
    </FilterPanel>
  );
};
